import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { type ChannelVisitsOverviewRowDataType, useChannelVisitsOverview } from '@/api/channel';
import { DataTable, type DataTableColumnDef } from '@/components/DataTable';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import { numberFormat } from '@/helper/numberFormatter';

type Props = {
  channelId?: string;
};

const ChannelVisitsOverviewTable = ({ channelId }: Props) => {
  const { pageFilters } = usePageFilterContext();
  const { startDate, endDate, audience } = pageFilters;
  const { channelVisitsData, channelVisitsSummary, isLoading, error } = useChannelVisitsOverview(
    channelId,
    startDate,
    endDate,
    audience?.id,
  );

  const showAudience = !!audience?.id;

  const columns = useMemo<DataTableColumnDef<ChannelVisitsOverviewRowDataType>[]>(
    () => [
      {
        header: t`Vendor`,
        id: 'vendor.name',
        accessorKey: 'vendor.name',
        enableSorting: true,
        sortingFn: (rowA, rowB) =>
          rowA.original.vendor.name
            .toLowerCase()
            .localeCompare(rowB.original.vendor.name.toLowerCase()),
        minSize: 140,
        flex: 2,
        footer: () => <Trans>Total</Trans>,
        cell: ({ row }) => row.original.vendor?.name ?? '',
      },
      {
        header: t`Visits`,
        id: 'visits.all',
        accessorKey: 'visits.all',
        meta: { align: 'end' },
        enableSorting: true,
        sortingFn: (rowA, rowB) => rowA.original.visits.all - rowB.original.visits.all,
        minSize: 120,
        flex: 1,
        footer: () => numberFormat(channelVisitsSummary?.visits.all),
        cell: ({ row }) => numberFormat(row.original.visits.all),
      },
      {
        header: audience?.name,
        id: 'visits.audience',
        accessorKey: 'visits.audience',
        meta: { align: 'end' },
        hidden: !showAudience,
        enableSorting: true,
        sortingFn: (rowA, rowB) => rowA.original.visits.audience - rowB.original.visits.audience,
        minSize: 120,
        flex: 1,
        footer: () => numberFormat(channelVisitsSummary?.visits.audience),
        cell: ({ row }) => numberFormat(row.original.visits.audience),
      },
      {
        header: t`TAM Visits`,
        id: 'visits.tam',
        accessorKey: 'visits.tam',
        meta: { align: 'end' },
        hidden: showAudience,
        enableSorting: true,
        sortingFn: (rowA, rowB) => rowA.original.visits.tam - rowB.original.visits.tam,
        minSize: 120,
        flex: 1,
        footer: () => numberFormat(channelVisitsSummary?.visits.tam),
        cell: ({ row }) => numberFormat(row.original.visits.tam),
      },
      {
        header: t`$/Visit`,
        id: 'spendPerVisit.all',
        accessorKey: 'spendPerVisit.all',
        meta: { align: 'end' },
        enableSorting: true,
        sortingFn: (rowA, rowB) =>
          rowA.original.spendPerVisit.all - rowB.original.spendPerVisit.all,
        minSize: 120,
        flex: 1,
        footer: () => numberFormat(channelVisitsSummary?.spendPerVisit.all, { isCurrency: true }),
        cell: ({ row }) => numberFormat(row.original.spendPerVisit.all, { isCurrency: true }),
      },
      {
        header: t`${audience?.name ?? t`Audience`} $/Visit`,
        id: 'spendPerVisit.audience',
        accessorKey: 'spendPerVisit.audience',
        meta: { align: 'end' },
        hidden: !showAudience,
        enableSorting: true,
        sortingFn: (rowA, rowB) =>
          rowA.original.spendPerVisit.audience - rowB.original.spendPerVisit.audience,
        minSize: 140,
        flex: 1,
        footer: () =>
          numberFormat(channelVisitsSummary?.spendPerVisit.audience, { isCurrency: true }),
        cell: ({ row }) => numberFormat(row.original.spendPerVisit.audience, { isCurrency: true }),
      },
      {
        header: t`TAM $/Visit`,
        id: 'spendPerVisit.tam',
        accessorKey: 'spendPerVisit.tam',
        meta: { align: 'end' },
        hidden: showAudience,
        enableSorting: true,
        sortingFn: (rowA, rowB) =>
          rowA.original.spendPerVisit.tam - rowB.original.spendPerVisit.tam,
        minSize: 120,
        flex: 1,
        footer: () => numberFormat(channelVisitsSummary?.spendPerVisit.tam, { isCurrency: true }),
        cell: ({ row }) => numberFormat(row.original.spendPerVisit.tam, { isCurrency: true }),
      },
    ],
    [audience?.name, channelVisitsSummary, showAudience],
  );

  return (
    <Panel title={<Trans>Visits Overview</Trans>} verifyC99Tag>
      <DataTable<ChannelVisitsOverviewRowDataType>
        loading={isLoading}
        error={error}
        emptyMessage={<Trans>No channel data was found.</Trans>}
        columns={columns}
        data={channelVisitsData ?? []}
        pagination={false}
        getRowId={(record) => record.vendor.id}
        defaultSorting={[{ id: 'visits.all', desc: true }]}
        initialColumnPinning={{
          left: ['vendor.name'],
        }}
      />
    </Panel>
  );
};

export default ChannelVisitsOverviewTable;
